import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import { useUser } from '../../../contexts/UserContext';
import { useNetwork } from '../../../utils/network';
import { useShowSnackbar } from '../../../utils/snackbar';
import { RecoveryCodesPanel } from './RecoveryCodesPanel';
import { PT, dialogPaperSx, fieldSx, ghostBtnSx, kickerSx, primaryBtnSx } from '../../../components/portalUi';

export type RecoveryCodesStatus = { remaining: number; generatedAt: string | null };

/* The one dialog that mints recovery codes, shared by Settings → Security and by the
 * save-your-codes prompt on arrival. A new set replaces the old one on the server,
 * which asks for the password — and, with 2FA on, a current authenticator code — so
 * a session left open on someone else's screen cannot mint codes. The codes are
 * shown once, in the RecoveryCodesPanel the 2FA setup uses, and from then on Done is
 * the only way out. */
export const RecoveryCodesGenerateDialog = ({
  open,
  onClose,
  onGenerated,
  intro = 'This replaces your current recovery codes: the old ones stop working straight away. Confirm it’s you to continue.'
}: {
  open: boolean;
  onClose: () => void;
  onGenerated?: (status: RecoveryCodesStatus) => void;
  intro?: string;
}) => {
  const { user } = useUser();
  const { post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [generating, setGenerating] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);

  const needsCode = Boolean(user?.hasTwoFactorAuthEnabled);
  const canGenerate = password.length > 0 && (!needsCode || code.length === 6);

  const closeForm = () => {
    setPassword('');
    setCode('');
    onClose();
  };

  const generate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canGenerate || generating) return;
    setGenerating(true);
    try {
      const result = await post(
        '/user/otp/recovery-codes',
        { password, ...(needsCode ? { twoFactorToken: code } : {}) },
        true
      );
      const fresh = Array.isArray(result?.codes) ? result.codes.map(String) : [];
      if (!fresh.length) {
        throw new Error('No codes came back. Please try again.');
      }
      setCodes(fresh);
      onGenerated?.({
        remaining: typeof result.remaining === 'number' ? result.remaining : fresh.length,
        generatedAt: result.generatedAt ?? new Date().toISOString()
      });
      setPassword('');
      setCode('');
    } catch (error: any) {
      // useNetwork throws a plain Error carrying the server's message — never an
      // axios `.response` (a wrong password, a wrong code, or the rate limit).
      showSnackbar(error?.message || 'Could not generate recovery codes.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog
      open={open}
      // Once the codes are up, Done is the only way out — they are never shown again.
      onClose={codes ? undefined : closeForm}
      fullWidth
      // The codes get the 2FA setup modal's width: two fifteen-character columns
      // only just fit in xs.
      maxWidth={codes ? 'sm' : 'xs'}
      scroll="paper"
      PaperProps={{ sx: dialogPaperSx }}
      TransitionProps={{ onExited: () => setCodes(null) }}
    >
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
        <Typography sx={kickerSx}>Recovery codes</Typography>
        <Typography sx={{ mt: 0.75, fontWeight: 800, fontSize: 18, color: PT.text, lineHeight: 1.25 }}>
          {codes ? 'Save your new codes' : 'Generate new codes'}
        </Typography>
      </Box>

      {codes ? (
        <DialogContent sx={{ px: 3, pt: 0.5, pb: 2.5 }}>
          <RecoveryCodesPanel codes={codes} onDone={onClose} />
        </DialogContent>
      ) : (
        <>
          <DialogContent sx={{ px: 3, pt: 0.5, pb: 1 }}>
            <Box component="form" id="recovery-codes-form" onSubmit={generate} noValidate>
              <Typography sx={{ fontSize: 14, color: PT.textMuted, lineHeight: 1.55, mb: 2.5 }}>{intro}</Typography>
              <TextField
                fullWidth
                type="password"
                label="Password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={fieldSx}
              />
              {needsCode && (
                <TextField
                  fullWidth
                  label="Authenticator code"
                  placeholder="000000"
                  helperText="The 6-digit code from your authenticator app"
                  value={code}
                  // Digits only, capped here rather than by maxLength — which would cut a
                  // pasted "123 456" to "123 45" before the space came out.
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
                  sx={{ ...fieldSx, mt: 2 }}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button onClick={closeForm} sx={{ ...ghostBtnSx, px: 2.5, py: 1 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="recovery-codes-form"
              disabled={!canGenerate || generating}
              sx={{ ...primaryBtnSx, px: 3, py: 1 }}
            >
              {generating ? 'Generating…' : 'Generate'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
